import { Button } from "@/components/ui/baseUi/button"
import { Separator } from "@/components/ui/baseUi/separator"
import { Text } from "@/components/ui/baseUi/text"
import type { BoxSessionDetailsEntity } from "@/modules/boxSlotSession/entities/boxSessionEntity"
import { cn } from "@/modules/lib/utils"
import type { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import type { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import type { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import { getDisplayExerciseCategory } from "@/modules/sessions/sessionDetails/blocks/exerciseBlock/exerciseUtils"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import { useUser } from "@/modules/users/hooks/useUser"
import { word } from "@modules/core/utils/i18n"
import { HeaderPodium } from "@modules/scores/scoreboard/components/headerPodium"
import { ScoreboardListView } from "@modules/scores/scoreboard/components/scoreboardListView"
import {
  ExerciseScoreCategories,
  getScoreDifficultyWording,
  ScoreDifficultyFilterEnum,
  ScoreType,
  type ExerciseScoreCategory,
} from "@modules/scores/scoreTypes"
import { SlidersHorizontalIcon } from "lucide-react"
import { motion } from "motion/react"
import { useState } from "react"

type ScoreboardViewProps = {
  session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity | BoxSessionDetailsEntity
  className?: string
} & React.ComponentProps<"div">

export function ScoreboardView({ session, className, ...props }: ScoreboardViewProps) {
  const [areFiltersOpen, setAreFiltersOpen] = useState<boolean>(false)
  const [selectedExercise, setSelectedExercise] = useState<SessionBlockExerciseEntity>(
    session.blocks.filter((block) => block.type === SessionBlockType.SESSION_EXERCISE)[0]!.content,
  )
  const [categorySelected, setCategorySelected] = useState<ExerciseScoreCategory>(ExerciseScoreCategories[0])
  const [difficultySelected, setDifficultySelected] = useState<ScoreDifficultyFilterEnum>(ScoreDifficultyFilterEnum.ALL)

  const user = useUser()

  const filtersToggle = () => {
    setAreFiltersOpen(!areFiltersOpen)
  }

  const onSelectExercise = (exercise: SessionBlockExerciseEntity) => {
    setSelectedExercise(exercise)
  }

  return (
    <div className={cn("flex w-full flex-col gap-2 overflow-hidden px-5 pt-18 pb-6", className)} {...props}>
      <div className="flex w-full flex-col gap-2">
        <Text fontStyle="TL">{word("program.planning.create.exercise.scoreboard")}</Text>
        {/* List of exercises */}
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-row flex-wrap gap-2">
            {session.blocks
              .filter((block) => block.type === SessionBlockType.SESSION_EXERCISE)
              .filter((block) => block.content.scoreSetConfigurations.length > 0)
              .map((exerciseBlock) => (
                <Button
                  key={exerciseBlock.content.id}
                  variant={selectedExercise?.id === exerciseBlock.content.id ? "primary" : "outline"}
                  onClick={() => {
                    onSelectExercise(exerciseBlock.content)
                  }}
                  size="sm"
                >
                  {getDisplayExerciseCategory(exerciseBlock.content.category, exerciseBlock.content.otherCategory)}
                </Button>
              ))}
          </div>

          <Button variant="ghost" size="iconMd" onClick={filtersToggle} className="focus:ring-0">
            <SlidersHorizontalIcon className="size-5" />
          </Button>
        </div>

        <Separator />

        {/* Filters by difficulty & category */}
        <motion.div
          initial={false}
          animate={{ height: areFiltersOpen ? "auto" : 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="mb-4 flex w-full flex-col gap-2 overflow-hidden"
        >
          {selectedExercise?.scoreSetConfigurations.some(
            (scoreSetConfiguration) =>
              scoreSetConfiguration.scoreType !== ScoreType.TEXT_FROM_ATHLETE &&
              scoreSetConfiguration.scoreType !== ScoreType.NO_SCORE,
          ) && (
            <div className="flex flex-col gap-1">
              <Text fontStyle="LS" className="text-textDefault-body">
                {word("scoreboard.filters.division").toUpperCase()}
              </Text>

              <div className="flex flex-row gap-2">
                {Object.values(ScoreDifficultyFilterEnum).map((difficulty, index) => {
                  return (
                    <Button
                      key={difficulty + index}
                      variant={difficultySelected === difficulty ? "primary" : "outline"}
                      onClick={() => setDifficultySelected(difficulty)}
                      size="sm"
                    >
                      {getScoreDifficultyWording(difficulty)}
                    </Button>
                  )
                })}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <Text fontStyle="LS" className="text-textDefault-body">
              {word("scoreboard.filters.category").toUpperCase()}
            </Text>
            <div className="flex flex-row gap-2 py-2 pl-1">
              <Button
                variant={categorySelected === ExerciseScoreCategories[0] ? "primary" : "outline"}
                onClick={() => setCategorySelected(ExerciseScoreCategories[0])}
                size="sm"
              >
                {word("scoreboard.filters.all")}
              </Button>

              <Button
                variant={categorySelected === ExerciseScoreCategories[1] ? "primary" : "outline"}
                onClick={() => setCategorySelected(ExerciseScoreCategories[1])}
                size="sm"
              >
                {word("scoreboard.filters.male")}
              </Button>

              <Button
                variant={categorySelected === ExerciseScoreCategories[2] ? "primary" : "outline"}
                onClick={() => setCategorySelected(ExerciseScoreCategories[2])}
                size="sm"
              >
                {word("scoreboard.filters.female")}
              </Button>
            </div>
          </div>
          {user?.genderCategory?.value === null && (
            <Text
              fontStyle="BS"
              color="textDefault-body"
              className="mt-[-8px] mb-4 whitespace-pre-line text-textDefault-body"
            >
              {word("scoreboard.filters.errorMessage.category")}
            </Text>
          )}
        </motion.div>

        {/* Scoreboard podium */}
        {selectedExercise && categorySelected && (
          <HeaderPodium
            session={session}
            selectedExercise={selectedExercise}
            selectedDifficulty={difficultySelected}
            categorySelected={categorySelected}
          />
        )}
      </div>

      {selectedExercise && categorySelected && (
        <ScoreboardListView
          session={session}
          selectedExercise={selectedExercise}
          selectedDifficulty={difficultySelected}
          categorySelected={categorySelected}
        />
      )}
    </div>
  )
}
